import { FocusTrap } from 'focus-trap-react';
import React, { useState, useCallback, FC } from 'react';
import { Icon } from '../shared';

import './modal.scss';
import type { FCC } from '../../utils/types';

const ModalContext = React.createContext<IModalContext | null>(null);

const useModal = () => {
  const context = React.useContext(ModalContext);
  if (context === null) {
    throw Error('You must use this within the boundaries of the ModalProvider');
  }
  return context;
};

interface IModalContext {
  open: (modalComponent: React.ReactNode) => void;
  close: () => void;
}

const CloseButton: FC<{ close: () => void }> = ({ close }) => {
  return (
    <button className="close-btn" onClick={close} aria-label="Lukk dialogrute">
      <Icon type="close" size={2.5} />
    </button>
  );
};

// TODO: replace with generic dialog from BringYourOwnDevice
export const Modal: FCC = ({ children }) => {
  const [modalComponent, setModalComponent] = useState<React.ReactNode>(null);

  const open = useCallback((modalComponent: React.ReactNode) => setModalComponent(modalComponent), []);

  const close = useCallback(() => {
    setModalComponent(null);
  }, []);

  return (
    <ModalContext.Provider value={{ open, close }}>
      {!!modalComponent && (
        <FocusTrap
          focusTrapOptions={{
            escapeDeactivates: true,
            clickOutsideDeactivates: true,
          }}
        >
          <div className="modal-box" role="dialog" aria-modal="true">
            <CloseButton close={close} />
            {modalComponent}
          </div>
        </FocusTrap>
      )}
      {/** biome-ignore lint/a11y/noStaticElementInteractions: backdrop click should close modal */}
      {!!modalComponent && <div className="backdrop" onClick={() => close()} />}
      {children}
    </ModalContext.Provider>
  );
};

export default useModal;
